import { type Item, POA_STATUSES, type Poa, type PoaInput, type PoaStatus } from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Plus, Printer, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { date, todayLocal } from '../../lib/format';
import { counterpartiesQuery } from '../../lib/projects';
import { Button, IconButton } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { documentsQuery, itemsQuery, parseNum, peopleQuery, qty, warehousesQuery } from './queries';

const poaQuery = (status: PoaStatus | '') =>
  queryOptions({
    queryKey: ['wh', 'poa', status],
    queryFn: () => api<Poa[]>('/warehouse/poa', { query: { status: status || undefined } }),
  });

const TONE: Record<PoaStatus, 'success' | 'info' | 'neutral'> = {
  active: 'success',
  used: 'info',
  cancelled: 'neutral',
};

/** Доверенности Д-1 на получение ТМЦ от поставщика (СК-09): список, выписка, печать с QR, отметка о получении */
export function PoaPanel() {
  const qc = useQueryClient();
  const search = useSearch({ strict: false }) as { poa?: string };
  const warehouses = useQuery(warehousesQuery);
  const canOperate = (warehouses.data ?? []).some((w) => w.canOperate);
  const [status, setStatus] = useState<PoaStatus | ''>('active');
  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState<Poa | null>(null);
  const [reason, setReason] = useState('');
  const r = useQuery(poaQuery(status));
  // По QR с печатной формы открывается список с нужной доверенностью: показываем все статусы
  useEffect(() => {
    if (search.poa) setStatus('');
  }, [search.poa]);
  const [using, setUsing] = useState<Poa | null>(null);
  const [printed, setPrinted] = useState<Poa | null>(null);
  // Подсвеченная по QR строка прокручивается в экран — на телефоне панель стоит под списком документов
  const highlighted = useRef<HTMLLIElement>(null);
  const loaded = Boolean(r.data);
  useEffect(() => {
    if (search.poa && loaded && highlighted.current) highlighted.current.scrollIntoView({ block: 'center' });
  }, [search.poa, loaded]);
  const cancel = useMutation({
    mutationFn: (p: Poa) => api<Poa>(`/warehouse/poa/${p.id}/cancel`, { body: { reason: reason.trim() } }),
    onSuccess: (p) => {
      toast(`${p.number} аннулирована`);
      setCancelling(null);
      setReason('');
      qc.invalidateQueries({ queryKey: ['wh', 'poa'] });
    },
  });
  const rows = r.data ?? [];
  return (
    <Panel
      className="mt-4"
      title="Доверенности Д-1"
      meta="на получение ТМЦ от поставщика; печать с QR"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Select
            className="h-11 w-auto"
            value={status}
            onChange={(e) => setStatus(e.target.value as PoaStatus | '')}
          >
            <option value="">Все</option>
            {(Object.keys(POA_STATUSES) as PoaStatus[]).map((k) => (
              <option key={k} value={k}>
                {POA_STATUSES[k]}
              </option>
            ))}
          </Select>
          {canOperate ? (
            <Button
              variant="primary"
              className="min-h-11"
              icon={<Plus className="size-4" />}
              onClick={() => setCreating(true)}
            >
              Выписать
            </Button>
          ) : null}
        </div>
      }
      bodyClassName="p-0"
    >
      {r.isPending ? (
        <div className="p-5">
          <Spinner />
        </div>
      ) : r.isError ? (
        <ErrorBox error={r.error} className="m-5" />
      ) : !rows.length ? (
        <div className="p-5">
          <EmptyState title="Доверенностей нет">
            Кнопка «Выписать» — доверенность сотруднику на получение у поставщика.
          </EmptyState>
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((p) => (
            <li
              key={p.id}
              ref={search.poa === p.id ? highlighted : undefined}
              className={`flex flex-col gap-2 px-4 py-3 text-[13px] md:flex-row md:items-center md:gap-4 ${search.poa === p.id ? 'bg-accent-soft' : ''}`}
            >
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[14px] font-medium text-ink">{p.number}</span>
                  <Badge tone={p.expired ? 'danger' : TONE[p.status]}>
                    {p.expired ? 'Срок истёк' : POA_STATUSES[p.status]}
                  </Badge>
                </div>
                <div className="text-ink-2">
                  {p.employeeName}
                  {p.position ? `, ${p.position}` : ''} · от {date(p.issuedOn)} по {date(p.validUntil)} ·{' '}
                  {p.counterpartyName ?? '—'}
                  {p.basis ? ` · ${p.basis}` : ''}
                </div>
                <div className="text-ink-2">
                  {p.lines.map((l) => `${l.name} — ${qty(l.quantity)} ${l.unit}`).join('; ')}
                </div>
                {p.receiptDocumentId ? (
                  <Link
                    to="/warehouse/documents/$docId"
                    params={{ docId: p.receiptDocumentId }}
                    className="text-ink hover:underline"
                  >
                    Поступление {p.receiptNumber}
                  </Link>
                ) : null}
                {p.reason ? <div className="text-ink-2">Причина: {p.reason}</div> : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`/api/v1/warehouse/poa/${p.id}/print`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex h-11 items-center gap-2 rounded-md border border-line-strong bg-surface px-4 text-[14px] font-medium text-ink hover:underline"
                >
                  <Printer className="size-4" aria-hidden />
                  Печать
                </a>
                {p.canEdit ? (
                  <>
                    <Button className="min-h-11" onClick={() => setUsing(p)}>
                      Получено
                    </Button>
                    <IconButton
                      label="Аннулировать"
                      className="min-h-11 min-w-11"
                      onClick={() => setCancelling(p)}
                    >
                      <Trash2 className="size-4" />
                    </IconButton>
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {creating ? (
        <PoaDialog
          onClose={() => setCreating(false)}
          onSaved={(p) => {
            setCreating(false);
            setPrinted(p);
          }}
        />
      ) : null}
      {using ? <UseDialog poa={using} onClose={() => setUsing(null)} /> : null}
      {printed ? (
        <Dialog
          open
          onOpenChange={(o) => !o && setPrinted(null)}
          title={`Доверенность ${printed.number} выписана`}
          footer={
            <>
              <Button className="min-h-11" onClick={() => setPrinted(null)}>
                Закрыть
              </Button>
              <a
                href={`/api/v1/warehouse/poa/${printed.id}/print?auto=1`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-11 items-center gap-2 rounded-md bg-accent px-4 text-[14px] font-medium text-on-accent"
              >
                <Printer className="size-4" aria-hidden />
                Открыть печатную форму
              </a>
            </>
          }
        >
          <p className="text-[14px] text-ink-2">Печатная форма Д-1 с QR откроется в новой вкладке.</p>
        </Dialog>
      ) : null}
      {cancelling ? (
        <Dialog
          open
          onOpenChange={(o) => !o && setCancelling(null)}
          title={`Аннулировать ${cancelling.number}`}
          footer={
            <>
              <Button className="min-h-11" onClick={() => setCancelling(null)}>
                Отмена
              </Button>
              <Button
                variant="danger"
                className="min-h-11"
                disabled={reason.trim().length < 3}
                loading={cancel.isPending}
                onClick={() => cancel.mutate(cancelling)}
              >
                Аннулировать
              </Button>
            </>
          }
        >
          <Field label="Причина">
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} autoFocus />
          </Field>
          {cancel.isError ? <ErrorBox error={cancel.error} className="mt-3" /> : null}
        </Dialog>
      ) : null}
    </Panel>
  );
}

/** Отметка «получено»: по желанию — ссылка на проведённое поступление того же склада */
function UseDialog({ poa, onClose }: { poa: Poa; onClose: () => void }) {
  const qc = useQueryClient();
  const [receiptId, setReceiptId] = useState('');
  const receipts = useQuery(documentsQuery({ kind: 'receipt', warehouseId: poa.warehouseId }));
  const m = useMutation({
    mutationFn: () =>
      api<Poa>(`/warehouse/poa/${poa.id}/use`, { body: { receiptDocumentId: receiptId || null } }),
    onSuccess: (p) => {
      toast(`${p.number}: получено`);
      qc.invalidateQueries({ queryKey: ['wh', 'poa'] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${poa.number}: получено`}
      description="Доверенность закрывается; назад не возвращается."
      footer={
        <>
          <Button className="min-h-11" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" className="min-h-11" loading={m.isPending} onClick={() => m.mutate()}>
            Подтвердить
          </Button>
        </>
      }
    >
      <Field label="Поступление по этой доверенности" hint="необязательно: проведённые поступления на склад">
        <Select value={receiptId} onChange={(e) => setReceiptId(e.target.value)}>
          <option value="">Без ссылки</option>
          {(receipts.data ?? [])
            .filter((d) => d.status === 'posted' || d.status === 'accepted')
            .map((d) => (
              <option key={d.id} value={d.id}>
                {d.number} от {date(d.docDate)}
                {d.counterpartyName ? ` · ${d.counterpartyName}` : ''}
              </option>
            ))}
        </Select>
      </Field>
      {m.isError ? <ErrorBox error={m.error} className="mt-3" /> : null}
    </Dialog>
  );
}

function PoaDialog({ onClose, onSaved }: { onClose: () => void; onSaved: (p: Poa) => void }) {
  const qc = useQueryClient();
  const warehouses = useQuery(warehousesQuery);
  const people = useQuery(peopleQuery);
  const items = useQuery(itemsQuery());
  const cps = useQuery(counterpartiesQuery('supplier'));
  const operable = (warehouses.data ?? []).filter((w) => w.canOperate && w.kind !== 'transit');
  const plus15 = (() => {
    const d = new Date(`${todayLocal()}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 15);
    return d.toISOString().slice(0, 10);
  })();
  const [f, setF] = useState({
    warehouseId: operable[0]?.id ?? '',
    employeeUserId: '',
    position: '',
    idDocument: '',
    validUntil: plus15,
    counterpartyId: '',
    counterpartyName: '',
    basis: '',
    note: '',
    lines: [{ itemId: '', name: '', unit: 'шт', quantity: '' }],
  });
  useEffect(() => {
    if (!f.warehouseId && operable[0]) setF((s) => ({ ...s, warehouseId: operable[0]!.id }));
  }, [operable, f.warehouseId]);
  const setLine = (i: number, patch: Partial<(typeof f.lines)[number]>) =>
    setF({ ...f, lines: f.lines.map((l, k) => (k === i ? { ...l, ...patch } : l)) });
  const pickItem = (i: number, itemId: string) => {
    const it = (items.data ?? []).find((x: Item) => x.id === itemId);
    setLine(i, { itemId, ...(it ? { name: it.name, unit: it.unit } : {}) });
  };
  const m = useMutation({
    mutationFn: () => {
      const body: PoaInput = {
        warehouseId: f.warehouseId,
        employeeUserId: f.employeeUserId,
        position: f.position.trim() || null,
        idDocument: f.idDocument.trim() || null,
        validUntil: f.validUntil,
        counterpartyId: f.counterpartyId || null,
        counterpartyName: f.counterpartyId ? null : f.counterpartyName.trim() || null,
        basis: f.basis.trim() || null,
        note: f.note.trim() || null,
        lines: f.lines
          .filter((l) => l.name.trim() && l.quantity.trim())
          .map((l) => ({
            itemId: l.itemId || null,
            name: l.name.trim(),
            unit: l.unit.trim() || 'шт',
            quantity: parseNum(l.quantity),
          })),
      };
      return api<Poa>('/warehouse/poa', { body });
    },
    onSuccess: (p) => {
      toast(`Доверенность ${p.number} выписана`);
      qc.invalidateQueries({ queryKey: ['wh', 'poa'] });
      onSaved(p);
    },
  });
  // Строка с названием, но без правильного количества — ошибка, а не молчаливый пропуск
  const badLine = (l: (typeof f.lines)[number]) => Boolean(l.name.trim()) && !(parseNum(l.quantity) > 0);
  const ready =
    f.warehouseId &&
    f.employeeUserId &&
    f.validUntil &&
    (f.counterpartyId || f.counterpartyName.trim()) &&
    f.lines.some((l) => l.name.trim() && parseNum(l.quantity) > 0) &&
    !f.lines.some(badLine);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Доверенность Д-1"
      description="Сотруднику на получение ТМЦ от поставщика. После сохранения — печатная форма с QR."
      width="lg"
      footer={
        <>
          <Button className="min-h-11" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="min-h-11"
            disabled={!ready}
            loading={m.isPending}
            onClick={() => m.mutate()}
          >
            Выписать
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Склад получения">
          <Select value={f.warehouseId} onChange={(e) => setF({ ...f, warehouseId: e.target.value })}>
            {operable.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Действительна по">
          <Input
            type="date"
            min={todayLocal()}
            value={f.validUntil}
            onChange={(e) => setF({ ...f, validUntil: e.target.value })}
          />
        </Field>
        <Field label="Кому выдана">
          <Select
            value={f.employeeUserId}
            onChange={(e) => {
              const person = (people.data ?? []).find((p) => p.id === e.target.value);
              setF({ ...f, employeeUserId: e.target.value, position: f.position || person?.position || '' });
            }}
          >
            <option value="">Выберите сотрудника</option>
            {(people.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.fullName}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Должность">
          <Input value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} />
        </Field>
        <Field label="Удостоверение личности" hint="номер, кем и когда выдано" className="sm:col-span-2">
          <Input value={f.idDocument} onChange={(e) => setF({ ...f, idDocument: e.target.value })} />
        </Field>
        <Field label="На получение от">
          <Select value={f.counterpartyId} onChange={(e) => setF({ ...f, counterpartyId: e.target.value })}>
            <option value="">Контрагента нет в справочнике</option>
            {(cps.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        {f.counterpartyId ? null : (
          <Field label="Поставщик текстом">
            <Input
              value={f.counterpartyName}
              onChange={(e) => setF({ ...f, counterpartyName: e.target.value })}
            />
          </Field>
        )}
        <Field label="По документу" hint="договор, счёт, накладная" className="sm:col-span-2">
          <Input value={f.basis} onChange={(e) => setF({ ...f, basis: e.target.value })} />
        </Field>
      </div>
      <fieldset className="mt-4">
        <legend className="mb-2 text-[13px] font-medium text-ink">Что получить</legend>
        <div className="flex flex-col gap-2">
          {f.lines.map((l, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: строки формы без идентификатора
            <div key={i} className="grid gap-2 sm:grid-cols-[1fr_2fr_80px_100px_44px] sm:items-end">
              <Field label="Из каталога">
                <Select value={l.itemId} onChange={(e) => pickItem(i, e.target.value)}>
                  <option value="">—</option>
                  {(items.data ?? []).map((it: Item) => (
                    <option key={it.id} value={it.id}>
                      {it.code} {it.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Наименование">
                <Input value={l.name} onChange={(e) => setLine(i, { name: e.target.value })} />
              </Field>
              <Field label="Ед.">
                <Input value={l.unit} onChange={(e) => setLine(i, { unit: e.target.value })} />
              </Field>
              <Field label="Кол-во">
                <Input
                  inputMode="decimal"
                  value={l.quantity}
                  onChange={(e) => setLine(i, { quantity: e.target.value })}
                />
              </Field>
              <IconButton
                label="Убрать строку"
                className="min-h-11 min-w-11"
                disabled={f.lines.length === 1}
                onClick={() => setF({ ...f, lines: f.lines.filter((_, k) => k !== i) })}
              >
                <Trash2 className="size-4" />
              </IconButton>
            </div>
          ))}
          <Button
            className="min-h-11 self-start"
            icon={<Plus className="size-4" />}
            onClick={() =>
              setF({ ...f, lines: [...f.lines, { itemId: '', name: '', unit: 'шт', quantity: '' }] })
            }
          >
            Строка
          </Button>
        </div>
      </fieldset>
      <Field label="Примечание" className="mt-4">
        <Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </Field>
      {m.isError ? <ErrorBox error={m.error} className="mt-3" /> : null}
    </Dialog>
  );
}
